import { Trans } from 'react-i18next'
import { useBoolean } from 'react-use'
import clsx from 'clsx'

import { AIEmojisAPI } from '@makyoapp/makyo-sdk'
import {
  RATING_MAX_TYPE,
  RATING_MIN_TYPE,
  RatingColOptionsKey,
  RatingInc,
  RatingMax,
  RatingMin,
} from '@makyoapp/makyo-common'
import { Check16, ChevronDown, MiStar24, MiStarHalf24 } from '@makyoapp/makyo-icon/icon'
import { Chip, MaterialIconPicker, SingleIconSelect } from '@makyoapp/makyo-ui'

import { useCreateColumnContext } from '@/context/CreateColumnContext'

import { Fld, Lbl } from './shared'

/**
 * handle the increment field of rating column type
 */
function FieldIncrement() {
  const { id, value, setValue } = useCreateColumnContext()
  return (
    <Fld>
      <Lbl>
        <Trans>Step</Trans>
      </Lbl>
      <SingleIconSelect
        id={`${id}-rating-inc`}
        onClick={(opt) => {
          setValue({
            ratingOptions: {
              ...value.ratingOptions,
              ratingIncrement: opt.id as RatingInc,
            },
          })
        }}
        options={[
          {
            id: 'half',
            icon: () => <MiStarHalf24 />,
          },
          {
            id: 'full',
            icon: () => <MiStar24 />,
          },
        ]}
        selected={value.ratingOptions.ratingIncrement}
      />
    </Fld>
  )
}

/**
 * handle the rating icon field of rating column type
 */
function FieldRatingIcon() {
  const { id, value, setValue } = useCreateColumnContext()
  const [show, setShow] = useBoolean(false)
  return (
    <Fld>
      <Lbl>
        <Trans>Icon</Trans>
      </Lbl>
      <div className='w-full justify-end flex'>
        <MaterialIconPicker
          id={`${id}-rating-icon`}
          menuClassName='z-406'
          portalMenu
          value={value.ratingOptions.ratingIcon}
          show={show}
          toggle={(active, toggleProps, icon, fontType, fontVariationSettings) => {
            return (
              <div
                className={clsx('flex-cc rounded-sm sdd-toggle-normal sdd-toggle', active && 'mk-mip-toggle-active')}
                style={fontVariationSettings}
              >
                <span className={clsx(fontType)}>{icon}</span>
                <ChevronDown />
              </div>
            )
          }}
          searchMs={300}
          searchFilter={async (keyword) => {
            const aiSearch = await AIEmojisAPI.ReqFindEmoji(keyword)
            return aiSearch
          }}
          onClose={() => setShow(false)}
          onToggle={() => setShow(!show)}
          onDone={(ratingIcon) => {
            setValue({
              ratingOptions: {
                ...value.ratingOptions,
                ratingIcon,
              },
            })
            setShow(false)
          }}
          clickAsDone={true}
          settings={{
            fill: 1,
            opsz: 24,
            grade: 0,
            wght: 400,
          }}
          toggleClassName={clsx('')}
        />
      </div>
    </Fld>
  )
}

interface FieldValueRatingProps {
  type: 'min' | 'max'
}

/**
 * handle the rating min / rating max field of rating column type
 */
function FieldValueRating({ type }: FieldValueRatingProps) {
  const { id, value, setValue } = useCreateColumnContext()
  let key: RatingColOptionsKey = 'ratingMax'
  let options: readonly (RatingMax | RatingMin)[] = RATING_MAX_TYPE
  if (type === 'min') {
    key = 'ratingMin'
    options = RATING_MIN_TYPE
  } else {
    key = 'ratingMax'
    options = RATING_MAX_TYPE
  }

  const val: RatingMin | RatingMax = value.ratingOptions[key]

  return (
    <Fld>
      <Lbl>
        <Trans>{type === 'min' ? 'Min. Rating' : 'Max. Rating'}</Trans>
      </Lbl>
      <div id={`${id}-rating-${type}`} className='mk-rating-wrap'>
        {Object.values(options).map((s) => {
          return (
            <Chip
              id={`${id}-rating-${type}-${s}`}
              className='cursor-pointer mk-rating-chip'
              active={s === val}
              key={s}
              onClick={() => {
                setValue({
                  ratingOptions: {
                    ...value.ratingOptions,
                    [key]: s,
                  },
                })
              }}
              after={s === val && <Check16 />}
            >
              {s}
            </Chip>
          )
        })}
      </div>
    </Fld>
  )
}

/**
 * handle all fields for rating column type
 */
export default function FieldRating() {
  const { value } = useCreateColumnContext()

  return (
    <>
      <FieldValueRating type='min' />
      <FieldValueRating type='max' />
      <FieldIncrement />
    </>
  )
}
